import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/status";
import { usePractice } from "@/pages/StaffApp";
import ApptCard from "@/components/ApptCard";
import AppointmentDialog from "@/components/AppointmentDialog";
import FacilitiesTab from "@/components/FacilitiesTab";
import PatientChartSheet from "@/components/PatientChartSheet";
import NewPacketDialog from "@/components/NewPacketDialog";
import { useNavigate } from "react-router-dom";
import { DefaultPacketPreview, MyDefaultPacketButton } from "@/components/DefaultPacketPreview";

const TAB_COLORS = ["#2563EB", "#DB2777", "#059669", "#D97706", "#7C3AED", "#0891B2", "#DC2626", "#4F46E5"];
const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (s, n) => { const d = new Date(`${s}T12:00:00`); d.setDate(d.getDate() + n); return iso(d); };
const label = (s) => `${new Date(`${s}T12:00:00`).toLocaleDateString(undefined, { weekday: "short" })} ${fmtDate(s)}`;
const addMonths = (s, n) => { const d = new Date(`${s.slice(0, 7)}-01T12:00:00`); d.setMonth(d.getMonth() + n); return iso(d); };

function useAppts(start, end, provider) {
  const { facility } = usePractice();
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/appointments", { params: { start, end, provider, facility } }).then((r) => setRows(r.data.items)).catch((e) => toast.error(errMsg(e))), [start, end, provider, facility]);
  useEffect(() => { load(); }, [load]);
  return [rows, load];
}

function DateNav({ day, setDay, step }) {
  const go = (dir) => setDay(step === "month" ? addMonths(day, dir) : addDays(day, dir * step));
  return (
    <div className="flex items-center gap-2">
      <Button variant="outline" size="icon" onClick={() => go(-1)} data-testid="schedule-prev"><ChevronLeft className="w-4 h-4" /></Button>
      <Input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} className="w-40" data-testid="schedule-date" />
      <Button variant="outline" size="icon" onClick={() => go(1)} data-testid="schedule-next"><ChevronRight className="w-4 h-4" /></Button>
      <Button variant="ghost" size="sm" onClick={() => setDay(iso(new Date()))} data-testid="schedule-today">Today</Button>
    </div>
  );
}

function DayBoard({ day, onNew, fac }) {
  const { practice } = usePractice();
  const [rows, reload] = useAppts(day, day, "");
  return (
    <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4" data-testid="day-board">
      {practice.providers.map((p, i) => {
        const mine = rows.filter((a) => a.provider_code === p.code);
        return (
          <div key={p.code} className="rounded-xl bg-white border overflow-hidden" data-testid={`day-col-${p.code}`}>
            <div className="px-3 py-2 text-white flex items-center" style={{ background: TAB_COLORS[i % TAB_COLORS.length] }}>
              <span className="font-semibold text-sm truncate flex-1">{p.name}</span><span className="text-xs bg-white/25 rounded-full px-2">{mine.length}</span>
              <button className="ml-2" onClick={() => onNew({ date: day, provider_code: p.code })} aria-label="Add" data-testid={`day-add-${p.code}`}><Plus className="w-4 h-4" /></button>
            </div>
            <div className="p-2 space-y-2 min-h-[80px]">{mine.map((a) => <ApptCard key={a.id} a={a} facilityName={fac(a.facility_code)} reload={reload} />)}
              {!mine.length && <p className="text-xs text-slate-400 p-2">No visits</p>}</div>
          </div>
        );
      })}
    </div>
  );
}

function ProviderWeek({ provider, day, onNew, fac, bump, view, setView }) {
  const { facility, can, my_provider_code: mine } = usePractice();
  const [pv, setPv] = useState(0);
  const home = facility || provider.facility_codes?.[0] || "";
  return (
    <div>
      <div className="mb-3 max-w-3xl"><DefaultPacketPreview facility={home} provider={provider.code} bump={pv} testid={`provider-default-packet-${provider.code}`}>
        {can("default_packet:own") && mine === provider.code && <MyDefaultPacketButton onSaved={() => setPv(pv + 1)} />}
      </DefaultPacketPreview></div>
      <div className="flex gap-1 mb-3" data-testid={`provider-view-${provider.code}`}>{[["day", "Day"], ["week", "Week"], ["month", "Month"]].map(([v, l]) => (
        <Button key={v} size="sm" variant={view === v ? "default" : "outline"} onClick={() => setView(v)} data-testid={`provider-view-${v}`}>{l}</Button>))}</div>
      {view === "day" && <ProviderDay provider={provider} day={day} onNew={onNew} fac={fac} bump={bump} />}
      {view === "week" && <WeekGrid provider={provider} day={day} onNew={onNew} fac={fac} bump={bump} />}
      {view === "month" && <MonthGrid provider={provider} day={day} onNew={onNew} fac={fac} bump={bump} />}
    </div>
  );
}

function ProviderDay({ provider, day, onNew, fac, bump }) {
  const [rows, reload] = useAppts(day, day, provider.code);
  useEffect(() => { reload(); }, [bump, reload]);
  return (
    <div className="rounded-xl bg-white border max-w-2xl" data-testid={`provider-day-${provider.code}`}>
      <div className="px-3 py-2 border-b flex items-center font-semibold text-sm"><span className="flex-1">{label(day)} - {rows.length} visits, {rows.reduce((s, a) => s + (a.duration_min || 0), 0)} min booked</span>
        <button onClick={() => onNew({ date: day, provider_code: provider.code })} className="text-brand" aria-label="Add" data-testid={`day-view-add-${provider.code}`}><Plus className="w-4 h-4" /></button></div>
      <div className="p-2 space-y-2">{rows.map((a) => <ApptCard key={a.id} a={a} facilityName={fac(a.facility_code)} reload={reload} />)}
        {!rows.length && <p className="text-sm text-slate-400 p-2">No visits this day</p>}</div>
    </div>
  );
}

function MonthGrid({ provider, day, onNew, fac, bump }) {
  const first = `${day.slice(0, 7)}-01`;
  const start = addDays(first, -new Date(`${first}T12:00:00`).getDay());
  const cells = useMemo(() => Array.from({ length: 42 }, (_, i) => addDays(start, i)), [start]);
  const [rows, reload] = useAppts(cells[0], cells[41], provider.code);
  const [open, setOpen] = useState(null);
  useEffect(() => { reload(); }, [bump, reload]);
  return (
    <div data-testid={`provider-month-${provider.code}`}>
      <p className="font-semibold mb-2">{new Date(`${first}T12:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" })}</p>
      <div className="grid grid-cols-7 gap-1 text-[11px] uppercase text-slate-500 mb-1">{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((w) => <span key={w} className="px-1">{w}</span>)}</div>
      <div className="grid grid-cols-7 gap-1">{cells.map((d) => {
        const mine = rows.filter((a) => a.date === d);
        return (
          <button key={d} onClick={() => setOpen(d)} data-testid={`month-cell-${d}`}
            className={`min-h-[72px] text-left rounded-lg border p-1 text-xs transition-colors hover:border-indigo-300 ${d.slice(0, 7) === day.slice(0, 7) ? "bg-white" : "bg-slate-50 text-slate-400"} ${d === day ? "ring-2 ring-indigo-400" : ""}`}>
            <span className="font-semibold">{Number(d.slice(8))}</span>
            {mine.length > 0 && <span className="ml-1 rounded-full bg-indigo-600 text-white px-1.5" data-testid={`month-count-${d}`}>{mine.length}</span>}
            <span className="hidden sm:block truncate mt-0.5">{mine.slice(0, 2).map((a) => a.patient_name).join(", ")}{mine.length > 2 ? "..." : ""}</span>
          </button>
        );
      })}</div>
      {open && (
        <div className="mt-4"><div className="flex justify-end mb-1"><Button size="sm" variant="ghost" onClick={() => setOpen(null)} data-testid="month-day-close">Close day</Button></div>
          <ProviderDay provider={provider} day={open} onNew={onNew} fac={fac} bump={bump} /></div>
      )}
    </div>
  );
}

function WeekGrid({ provider, day, onNew, fac, bump }) {
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(day, i)), [day]);
  const [rows, reload] = useAppts(days[0], days[6], provider.code);
  useEffect(() => { reload(); }, [bump, reload]);
  return (
    <div className="grid gap-2 grid-cols-1 md:grid-cols-7" data-testid={`provider-week-${provider.code}`}>
      {days.map((d) => {
        const mine = rows.filter((a) => a.date === d);
        return (
          <div key={d} className="rounded-xl bg-white border min-h-[140px]">
            <div className="px-2 py-1.5 border-b flex items-center text-xs font-semibold text-slate-700"><span className="flex-1">{label(d)}</span>
              <button onClick={() => onNew({ date: d, provider_code: provider.code })} className="text-brand" aria-label="Add" data-testid={`week-add-${provider.code}-${d}`}><Plus className="w-4 h-4" /></button></div>
            <div className="p-1.5 space-y-1.5">{mine.map((a) => <ApptCard key={a.id} a={a} facilityName={fac(a.facility_code)} reload={reload} />)}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function SchedulePage() {
  const { practice, can } = usePractice();
  const [day, setDay] = useState(iso(new Date()));
  const [tab, setTab] = useState("calendar");
  const [provView, setProvView] = useState("week");
  const step = tab === "calendar" ? 1 : provView === "day" ? 1 : provView === "month" ? "month" : 7;
  const [dlg, setDlg] = useState(null);
  const [bump, setBump] = useState(0);
  const [chartId, setChartId] = useState(null);
  const [packetFor, setPacketFor] = useState(null);
  const nav = useNavigate();
  const fac = (c) => practice.facilities.find((f) => f.code === c)?.name;
  const onNew = (init) => can("schedule:write") && setDlg(init);
  return (
    <div className="flex-1 overflow-y-auto p-4 lg:p-6 bg-gradient-to-br from-slate-50 via-white to-sky-50" data-testid="schedule-page">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2"><CalendarDays className="text-brand" />Schedule</h1>
        {tab !== "facilities" && <DateNav day={day} setDay={setDay} step={step} />}
        {can("schedule:write") && <Button className="btn-brand ml-auto" onClick={() => setDlg({ date: day })} data-testid="schedule-new-appt"><Plus className="w-4 h-4 mr-1" />Schedule patient</Button>}
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-nowrap md:flex-wrap overflow-x-auto w-full h-auto bg-slate-200/60 rounded-2xl gap-1.5 p-1.5 justify-start border border-slate-300/50" data-testid="schedule-tabs">
          <TabsTrigger value="calendar" className="rounded-full border data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-calendar">All providers</TabsTrigger>
          {practice.providers.map((p, i) => (
            <TabsTrigger key={p.code} value={p.code} style={{ "--tab": TAB_COLORS[i % TAB_COLORS.length] }}
              className="rounded-full border border-[color:var(--tab)] text-[color:var(--tab)] data-[state=active]:bg-[color:var(--tab)] data-[state=active]:text-white" data-testid={`tab-provider-${p.code}`}>{p.name}</TabsTrigger>))}
          <TabsTrigger value="facilities" className="rounded-full border data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="tab-facilities">Facilities & patients</TabsTrigger>
        </TabsList>
        <TabsContent value="calendar" className="mt-4">{tab === "calendar" && <DayBoard key={bump} day={day} onNew={onNew} fac={fac} />}</TabsContent>
        {practice.providers.map((p) => (
          <TabsContent key={p.code} value={p.code} className="mt-4">{tab === p.code && <ProviderWeek provider={p} day={day} onNew={onNew} fac={fac} bump={bump} view={provView} setView={setProvView} />}</TabsContent>))}
        <TabsContent value="facilities" className="mt-4">{tab === "facilities" && <FacilitiesTab onOpenChart={setChartId} />}</TabsContent>
      </Tabs>
      <AppointmentDialog open={!!dlg} initial={dlg} onClose={() => setDlg(null)} onSaved={() => setBump(bump + 1)} />
      <PatientChartSheet id={chartId} onClose={() => setChartId(null)} onNewPacket={(p) => { setChartId(null); setPacketFor(p); }} onOpenPacket={() => nav("/")} />
      {packetFor && <NewPacketDialog patient={packetFor} onClose={() => setPacketFor(null)} onAddPatient={() => {}} onCreated={() => { setPacketFor(null); toast.success("Packet is in the Workspace"); }} />}
    </div>
  );
}
